import { getPublicSiteOrigin } from './siteUrl';

const MANAGED = 'data-adde-seo';

function removeManaged(): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll(`[${MANAGED}="1"]`).forEach((el) => el.remove());
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  if (typeof document === 'undefined' || !content) return;
  const sel = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(sel) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED, '1');
    document.head.appendChild(el);
  } else if (!el.getAttribute(MANAGED)) {
    el.setAttribute(MANAGED, '1');
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, extra: Record<string, string> = {}): void {
  if (typeof document === 'undefined' || !href) return;
  const parts = [`link[rel="${rel}"]`];
  if (extra.hreflang) parts.push(`[hreflang="${extra.hreflang}"]`);
  const sel = parts.join('');
  let el = document.head.querySelector(sel) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED, '1');
    Object.entries(extra).forEach(([k, v]) => el?.setAttribute(k, v));
    document.head.appendChild(el);
  } else if (!el.getAttribute(MANAGED)) {
    el.setAttribute(MANAGED, '1');
  }
  el.setAttribute('href', href);
}

export type ApplyPublicDocumentSeoInput = {
  htmlLang: string;
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  robots: string;
  og: {
    type: string;
    siteName: string;
    title: string;
    description: string;
    url: string;
    image: string;
    locale: string;
    localeAlternate: string[];
  };
  twitter: {
    card: string;
    title: string;
    description: string;
  };
  jsonLdScripts?: string[];
};

/**
 * document head — SPA marshrut va til o‘zgarganda chaqiriladi.
 */
export function applyPublicDocumentSeo(input: ApplyPublicDocumentSeoInput): void {
  if (typeof document === 'undefined') return;

  removeManaged();

  document.documentElement.lang = input.htmlLang;
  document.title = input.title;

  upsertMeta('name', 'description', input.description);
  upsertMeta('name', 'keywords', input.keywords);
  upsertMeta('name', 'robots', input.robots);

  upsertMeta('property', 'og:type', input.og.type);
  upsertMeta('property', 'og:site_name', input.og.siteName);
  upsertMeta('property', 'og:title', input.og.title);
  upsertMeta('property', 'og:description', input.og.description);
  upsertMeta('property', 'og:url', input.og.url);
  upsertMeta('property', 'og:image', input.og.image);
  upsertMeta('property', 'og:locale', input.og.locale);
  input.og.localeAlternate.forEach((loc) => {
    const el = document.createElement('meta');
    el.setAttribute('property', 'og:locale:alternate');
    el.setAttribute('content', loc);
    el.setAttribute(MANAGED, '1');
    document.head.appendChild(el);
  });

  upsertMeta('name', 'twitter:card', input.twitter.card);
  upsertMeta('name', 'twitter:title', input.twitter.title);
  upsertMeta('name', 'twitter:description', input.twitter.description);

  upsertLink('canonical', input.canonicalUrl);

  const jsonList = input.jsonLdScripts ?? [];
  jsonList.forEach((json, i) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute(MANAGED, '1');
    s.setAttribute('id', `adde-seo-jsonld-${i}`);
    s.text = json;
    document.head.appendChild(s);
  });
}

export function clearPublicDocumentSeo(): void {
  removeManaged();
}

export function defaultOgImageUrl(): string {
  return `${getPublicSiteOrigin()}/adde-app-icon.png`;
}
